"use client"

import { useState, useEffect } from "react"
import { useSession } from "next-auth/react"
import Link from "next/link"
import {
    Trophy,
    Users,
    CheckCircle2,
    Clock,
    XCircle,
    Gift,
    Coins,
    Package,
    ExternalLink,
    Loader2,
    AlertCircle,
    Sparkles,
    Target,
    MessageSquare,
} from "lucide-react"

interface Milestone {
    id: number
    follower_target: number
    title: string
    description: string | null
    reward_type: string
    reward_amount: number
    reached: boolean
    claimed: boolean
}

interface Platform {
    id: number
    platform: string
    display_name: string
    url: string
    follower_count: number
    icon: string | null
    milestones: Milestone[]
}

interface UserFollow {
    platform_id: number
    social_handle: string
    status: string
    rewarded: boolean
    reward_coins: number
}

const platformIcons: Record<string, typeof MessageSquare> = {
    facebook: Users,
    discord: MessageSquare,
}

export default function SocialRewardsPage() {
    const { data: session } = useSession()
    const [platforms, setPlatforms] = useState<Platform[]>([])
    const [userFollows, setUserFollows] = useState<UserFollow[]>([])
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState<"milestones" | "follow">("milestones")

    // Follow form state
    const [followPlatform, setFollowPlatform] = useState<number | null>(null)
    const [socialHandle, setSocialHandle] = useState("")
    const [followSubmitting, setFollowSubmitting] = useState(false)
    const [followMessage, setFollowMessage] = useState("")

    // Claim state
    const [claimingId, setClaimingId] = useState<number | null>(null)
    const [claimMessage, setClaimMessage] = useState("")
    const [claimError, setClaimError] = useState("")
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const [successData, setSuccessData] = useState<{ message: string, amount: number, type: string } | null>(null)
    const [balance, setBalance] = useState<number | null>(null)

    // Character selection for packs
    const [characters, setCharacters] = useState<any[]>([])
    const [selectedCharId, setSelectedCharId] = useState<string>("")
    const [charsLoading, setCharsLoading] = useState(false)

    useEffect(() => {
        fetchData()
    }, [])

    const fetchData = async () => {
        try {
            const [milestonesRes, summaryRes] = await Promise.all([
                fetch("/api/social/milestones"),
                fetch("/api/dashboard/summary")
            ])

            if (milestonesRes.ok) {
                const data = await milestonesRes.json()
                setPlatforms(data.platforms || [])
                setUserFollows(data.userFollows || [])
            }

            if (summaryRes.ok) {
                const summaryData = await summaryRes.json()
                setBalance(summaryData.balance)
            }
        } catch {
            console.error("Failed to load social data")
        } finally {
            setLoading(false)
        }
    }

    const fetchCharacters = async () => {
        setCharsLoading(true)
        try {
            const res = await fetch("/api/dashboard/characters")
            if (res.ok) {
                const data = await res.json()
                let allChars: any[] = []
                if (data.accounts) {
                    data.accounts.forEach((acc: any) => {
                        if (acc.characters) {
                            acc.characters.forEach((char: any) => {
                                allChars.push({
                                    ...char,
                                    account_name: acc.login,
                                    server: acc.server_id
                                })
                            })
                        }
                    })
                }
                setCharacters(allChars)
            }
        } catch {
            console.error("Failed to fetch characters")
        } finally {
            setCharsLoading(false)
        }
    }

    useEffect(() => {
        if (session) {
            fetchCharacters()
        }
    }, [session])

    const handleClaim = async (milestoneId: number, gameAccount?: string, characterName?: string) => {
        setClaimingId(milestoneId)
        setClaimMessage("")
        setClaimError("")

        try {
            const res = await fetch("/api/social/milestones/claim", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    milestoneId,
                    gameAccount,
                    characterName
                }),
            })
            const data = await res.json()

            if (res.ok) {
                setSuccessData({
                    message: data.message,
                    amount: data.reward_amount,
                    type: data.reward_type
                })
                setShowSuccessModal(true)
                fetchData() // refresh
            } else {
                setClaimError(data.error || "Failed to claim")
            }
        } catch {
            setClaimError("An error occurred")
        } finally {
            setClaimingId(null)
        }
    }

    const handleFollowSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!followPlatform || !socialHandle.trim()) return

        setFollowSubmitting(true)
        setFollowMessage("")

        try {
            const res = await fetch("/api/social/follow", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    platformId: followPlatform,
                    socialHandle: socialHandle.trim(),
                }),
            })
            const data = await res.json()

            if (res.ok) {
                setFollowMessage(data.message)
                setSocialHandle("")
                setFollowPlatform(null)
                fetchData()
            } else {
                setFollowMessage(data.error || "Failed to submit")
            }
        } catch {
            setFollowMessage("An error occurred")
        } finally {
            setFollowSubmitting(false)
        }
    }

    const getUserFollow = (platformId: number) =>
        userFollows.find((f) => f.platform_id === platformId)

    const followStatusIcon = (status: string) => {
        switch (status) {
            case "verified":
                return <CheckCircle2 className="h-4 w-4 text-green-500" />
            case "rejected":
                return <XCircle className="h-4 w-4 text-red-500" />
            default:
                return <Clock className="h-4 w-4 text-amber-500" />
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
            </div>
        )
    }

    // Calculate total progress across all platforms
    const allMilestones = platforms.flatMap((p) => p.milestones)
    const reachedCount = allMilestones.filter((m) => m.reached).length
    const totalMilestones = allMilestones.length

    return (
        <div className="min-h-screen bg-transparent pt-24 pb-16">
            <div className="container mx-auto px-4 max-w-5xl">
                {/* Header */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-bold uppercase tracking-widest mb-4">
                        <Sparkles className="h-3 w-3" />
                        Community Rewards
                    </div>
                    <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight mb-3">
                        Social Rewards
                    </h1>
                    {balance !== null && (
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 border border-slate-800 rounded-xl mt-4">
                            <Coins className="h-4 w-4 text-amber-400" />
                            <span className="text-slate-400 text-sm font-medium">Your Balance:</span>
                            <span className="text-white font-bold">{balance.toLocaleString()} DC</span>
                        </div>
                    )}
                    <p className="text-slate-400 text-lg max-w-2xl mx-auto">
                        Follow us on social media and earn rewards! Help us grow and unlock
                        community milestones for everyone.
                    </p>
                </div>

                {/* Tab Switch */}
                <div className="flex justify-center mb-10">
                    <div className="inline-flex bg-slate-900 border border-slate-800 rounded-xl p-1">
                        <button
                            onClick={() => setActiveTab("milestones")}
                            className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-all ${activeTab === "milestones"
                                ? "bg-gradient-to-r from-[#681812] to-[#3E0E0A] border border-[#C88246]/60 text-[#EDE3D4] shadow-lg shadow-[#681812]/30"
                                : "text-slate-500 hover:text-slate-300"
                                }`}
                        >
                            <Trophy className="inline h-4 w-4 mr-2 -mt-0.5" />
                            Milestones
                        </button>
                        <button
                            onClick={() => setActiveTab("follow")}
                            className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-all ${activeTab === "follow"
                                ? "bg-gradient-to-r from-[#681812] to-[#3E0E0A] border border-[#C88246]/60 text-[#EDE3D4] shadow-lg shadow-[#681812]/30"
                                : "text-slate-500 hover:text-slate-300"
                                }`}
                        >
                            <Gift className="inline h-4 w-4 mr-2 -mt-0.5" />
                            Follow & Earn
                        </button>
                    </div>
                </div>

                {/* Success / Error messages */}
                {claimMessage && (
                    <div className="mb-6 p-4 rounded-xl bg-green-500/10 border border-green-500/30 text-green-400 text-sm text-center font-medium">
                        <CheckCircle2 className="inline h-4 w-4 mr-2" />
                        {claimMessage}
                    </div>
                )}
                {claimError && (
                    <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm text-center font-medium">
                        <AlertCircle className="inline h-4 w-4 mr-2" />
                        {claimError}
                    </div>
                )}

                {activeTab === "milestones" && (
                    <>
                        {/* Overall progress */}
                        {totalMilestones > 0 && (
                            <div className="mb-8 p-6 rounded-2xl bg-slate-900/50 border border-slate-800 text-center">
                                <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
                                    Community Progress
                                </div>
                                <div className="text-3xl font-black text-white mb-3">
                                    {reachedCount} / {totalMilestones}{" "}
                                    <span className="text-lg text-slate-500 font-medium">milestones reached</span>
                                </div>
                                <div className="w-full max-w-md mx-auto h-2 bg-slate-800 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-gradient-to-r from-[#C88246] to-[#E05A47] rounded-full transition-all duration-700"
                                        style={{ width: `${totalMilestones > 0 ? (reachedCount / totalMilestones) * 100 : 0}%` }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Platform Cards */}
                        {platforms.length === 0 ? (
                            <div className="text-center py-16">
                                <Target className="h-12 w-12 text-slate-700 mx-auto mb-4" />
                                <p className="text-slate-500">No social platforms configured yet. Check back soon!</p>
                            </div>
                        ) : (
                            <div className="space-y-10">
                                {platforms.map((platform) => {
                                    const Icon = platformIcons[platform.platform] || Users
                                    return (
                                        <div key={platform.id}>
                                            {/* Platform header */}
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 bg-slate-800 rounded-lg">
                                                        <Icon className="h-5 w-5 text-[#D8B478]" />
                                                    </div>
                                                    <div>
                                                        <h2 className="text-lg font-bold text-white">{platform.display_name}</h2>
                                                        <div className="text-sm text-slate-400">
                                                            <span className="text-[#D8B478] font-bold">
                                                                {platform.follower_count.toLocaleString()}
                                                            </span>{" "}
                                                            followers
                                                        </div>
                                                    </div>
                                                </div>
                                                <a
                                                    href={platform.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-sm font-semibold text-[#D8B478] transition-colors"
                                                >
                                                    Follow <ExternalLink className="h-3.5 w-3.5" />
                                                </a>
                                            </div>

                                            {/* Milestones timeline */}
                                            {platform.milestones.length === 0 ? (
                                                <div className="py-6 text-center text-slate-600 text-sm">
                                                    No milestones set for this platform yet.
                                                </div>
                                            ) : (
                                                <div className="space-y-3">
                                                    {platform.milestones.map((m) => {
                                                        const progress = Math.min(
                                                            100,
                                                            (platform.follower_count / m.follower_target) * 100
                                                        )
                                                        return (
                                                            <div
                                                                key={m.id}
                                                                className={`rounded-xl border p-5 transition-colors ${m.reached
                                                                    ? "bg-slate-900/80 border-green-500/30"
                                                                    : "bg-slate-900/40 border-slate-800"
                                                                    }`}
                                                            >
                                                                <div className="flex items-start justify-between gap-4 mb-3">
                                                                    <div className="flex items-center gap-3">
                                                                        <div
                                                                            className={`p-2 rounded-lg ${m.reached
                                                                                ? "bg-green-500/10"
                                                                                : "bg-slate-800"
                                                                                }`}
                                                                        >
                                                                            {m.reached ? (
                                                                                <CheckCircle2 className="h-5 w-5 text-green-500" />
                                                                            ) : (
                                                                                <Target className="h-5 w-5 text-slate-500" />
                                                                            )}
                                                                        </div>
                                                                        <div>
                                                                            <h3 className="font-bold text-white">{m.title}</h3>
                                                                            {m.description && (
                                                                                <p className="text-xs text-slate-500 mt-0.5">
                                                                                    {m.description}
                                                                                </p>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <div className="text-right shrink-0">
                                                                        <div className="flex items-center gap-1 text-amber-400 font-bold text-sm">
                                                                            {m.reward_type === "coins" ? (
                                                                                <Coins className="h-4 w-4" />
                                                                            ) : (
                                                                                <Package className="h-4 w-4" />
                                                                            )}
                                                                            {m.reward_amount.toLocaleString()}{" "}
                                                                            {m.reward_type === "coins" ? "DC" : "Pack"}
                                                                        </div>
                                                                        <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mt-1">
                                                                            {m.follower_target.toLocaleString()} followers
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Progress bar */}
                                                                <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden mb-3">
                                                                    <div
                                                                        className={`h-full rounded-full transition-all duration-700 ${m.reached
                                                                            ? "bg-green-500"
                                                                            : "bg-gradient-to-r from-[#C88246] to-[#E05A47]"
                                                                            }`}
                                                                        style={{ width: `${progress}%` }}
                                                                    />
                                                                </div>

                                                                <div className="flex items-center justify-between">
                                                                    <div className="text-xs text-slate-500">
                                                                        {platform.follower_count.toLocaleString()} /{" "}
                                                                        {m.follower_target.toLocaleString()}
                                                                    </div>

                                                                    {m.reached && session ? (
                                                                        m.claimed ? (
                                                                            <span className="text-xs font-bold text-green-500 flex items-center gap-1">
                                                                                <CheckCircle2 className="h-3 w-3" />
                                                                                Claimed
                                                                            </span>
                                                                        ) : (() => {
                                                                            const follow = getUserFollow(platform.id)
                                                                            const isVerified = follow?.status === "verified"

                                                                            if (!isVerified) {
                                                                                return (
                                                                                    <div className="text-right">
                                                                                        <span className="text-[10px] font-bold text-amber-500 bg-amber-500/10 px-2 py-0.5 rounded flex items-center gap-1">
                                                                                            <AlertCircle className="h-3 w-3" />
                                                                                            Follow Required
                                                                                        </span>
                                                                                        <button
                                                                                            onClick={() => setActiveTab("follow")}
                                                                                            className="text-[10px] text-[#D8B478] hover:underline mt-1"
                                                                                        >
                                                                                            Verify now
                                                                                        </button>
                                                                                    </div>
                                                                                )
                                                                            }

                                                                            return (
                                                                                <div className="flex flex-col items-end gap-2">
                                                                                    {m.reward_type === "pack" && (
                                                                                        <select
                                                                                            value={selectedCharId}
                                                                                            onChange={(e) => setSelectedCharId(e.target.value)}
                                                                                            className="bg-slate-800 border-slate-700 text-white text-[10px] rounded p-1 min-w-[120px]"
                                                                                        >
                                                                                            <option value="">Select Character</option>
                                                                                            {characters.map(char => (
                                                                                                <option key={`${char.server}:${char.char_name}`} value={`${char.server}:${char.char_name}`}>
                                                                                                    {char.char_name} ({char.server})
                                                                                                </option>
                                                                                            ))}
                                                                                        </select>
                                                                                    )}
                                                                                    <button
                                                                                        onClick={() => {
                                                                                            const char = characters.find(c => `${c.server}:${c.char_name}` === selectedCharId)
                                                                                            handleClaim(m.id, char?.account_name, char?.char_name)
                                                                                        }}
                                                                                        disabled={claimingId === m.id || (m.reward_type === "pack" && !selectedCharId)}
                                                                                        className="px-4 py-1.5 bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1"
                                                                                    >
                                                                                        {claimingId === m.id ? (
                                                                                            <Loader2 className="h-3 w-3 animate-spin" />
                                                                                        ) : (
                                                                                            <Gift className="h-3 w-3" />
                                                                                        )}
                                                                                        Claim Reward
                                                                                    </button>
                                                                                </div>
                                                                            )
                                                                        })()
                                                                    ) : m.reached ? (
                                                                        <Link
                                                                            href="/login"
                                                                            className="text-xs text-[#D8B478] font-semibold hover:underline"
                                                                        >
                                                                            Log in to claim
                                                                        </Link>
                                                                    ) : (
                                                                        <span className="text-xs text-slate-600 font-bold uppercase">
                                                                            {Math.floor(progress)}% complete
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </>
                )}

                {activeTab === "follow" && (
                    <div className="max-w-2xl mx-auto">
                        {!session ? (
                            <div className="text-center py-16 rounded-2xl border-2 border-dashed border-slate-800 bg-slate-900/30">
                                <Gift className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                                <h3 className="text-lg font-bold text-white mb-2">Log In to Participate</h3>
                                <p className="text-slate-500 text-sm mb-6">
                                    Follow us on social media, submit your handle, and earn Credits!
                                </p>
                                <Link
                                    href="/login"
                                    className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-[#681812] to-[#3E0E0A] border border-[#C88246]/60 text-white rounded-lg text-sm font-bold hover:brightness-125 transition-colors"
                                >
                                    Log In
                                </Link>
                            </div>
                        ) : (
                            <>
                                <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 mb-8">
                                    <h2 className="text-lg font-bold text-white mb-2">How it works</h2>
                                    <ol className="space-y-2 text-sm text-slate-400 list-decimal list-inside">
                                        <li>Follow us on a social media platform below</li>
                                        <li>Enter your social media handle/name so we can verify</li>
                                        <li>An admin will verify your follow and award you Credits</li>
                                    </ol>
                                </div>

                                {/* Platform cards with follow status */}
                                <div className="space-y-4">
                                    {platforms.map((platform) => {
                                        const Icon = platformIcons[platform.platform] || Users
                                        const follow = getUserFollow(platform.id)

                                        return (
                                            <div
                                                key={platform.id}
                                                className="rounded-xl border border-slate-800 bg-slate-900/50 p-5"
                                            >
                                                <div className="flex items-center justify-between mb-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="p-2 bg-slate-800 rounded-lg">
                                                            <Icon className="h-5 w-5 text-[#D8B478]" />
                                                        </div>
                                                        <div>
                                                            <h3 className="font-bold text-white">{platform.display_name}</h3>
                                                            <a
                                                                href={platform.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-xs text-[#D8B478] hover:underline flex items-center gap-1"
                                                            >
                                                                Visit page <ExternalLink className="h-3 w-3" />
                                                            </a>
                                                        </div>
                                                    </div>

                                                    {follow && (
                                                        <div className="flex items-center gap-2">
                                                            {followStatusIcon(follow.status)}
                                                            <span
                                                                className={`text-xs font-bold uppercase tracking-wider ${follow.status === "verified"
                                                                    ? "text-green-500"
                                                                    : follow.status === "rejected"
                                                                        ? "text-red-500"
                                                                        : "text-amber-500"
                                                                    }`}
                                                            >
                                                                {follow.status}
                                                            </span>
                                                            {follow.rewarded && follow.reward_coins > 0 && (
                                                                <span className="text-xs text-amber-400 font-bold ml-2">
                                                                    +{follow.reward_coins} DC
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>

                                                {follow ? (
                                                    <div className="text-sm text-slate-500 bg-slate-800/50 rounded-lg px-4 py-2">
                                                        Submitted as: <span className="text-slate-300 font-medium">{follow.social_handle}</span>
                                                    </div>
                                                ) : (
                                                    <form
                                                        onSubmit={(e) => {
                                                            setFollowPlatform(platform.id)
                                                            handleFollowSubmit(e)
                                                        }}
                                                        className="flex gap-3"
                                                    >
                                                        <input
                                                            type="text"
                                                            value={followPlatform === platform.id ? socialHandle : ""}
                                                            onFocus={() => setFollowPlatform(platform.id)}
                                                            onChange={(e) => {
                                                                setFollowPlatform(platform.id)
                                                                setSocialHandle(e.target.value)
                                                            }}
                                                            placeholder={
                                                                platform.platform === "discord"
                                                                    ? "Your Discord tag (e.g. User#1234)"
                                                                    : "Your Facebook profile name"
                                                            }
                                                            className="flex-1 rounded-lg border border-slate-700 bg-slate-800 px-4 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#C88246]"
                                                            required
                                                        />
                                                        <button
                                                            type="submit"
                                                            disabled={followSubmitting}
                                                            className="px-5 py-2 bg-gradient-to-r from-[#681812] to-[#3E0E0A] border border-[#C88246]/60 text-white text-sm font-bold rounded-lg hover:brightness-125 transition-colors disabled:opacity-50 shrink-0"
                                                        >
                                                            {followSubmitting ? (
                                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                            ) : (
                                                                "Submit"
                                                            )}
                                                        </button>
                                                    </form>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>

                                {followMessage && (
                                    <div className="mt-4 p-3 rounded-lg bg-slate-800/50 border border-slate-700 text-sm text-slate-300 text-center">
                                        {followMessage}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Success Modal */}
            {showSuccessModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-md">
                    <div className="bg-slate-900 border border-white/10 rounded-3xl w-full max-w-sm shadow-2xl relative overflow-hidden animate-in fade-in zoom-in duration-300">
                        {/* Glow effect */}
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-48 bg-[#681812]/20 rounded-full blur-[80px] -mt-24" />

                        <div className="p-8 flex flex-col items-center text-center relative z-10">
                            <div className="w-20 h-20 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-full flex items-center justify-center mb-6 border border-green-500/30">
                                {successData?.type === 'coins' ? (
                                    <Coins className="w-10 h-10 text-green-400" />
                                ) : (
                                    <Package className="w-10 h-10 text-green-400" />
                                )}
                            </div>

                            <h3 className="text-2xl font-black text-white mb-2 tracking-tight">
                                Success!
                            </h3>
                            <p className="text-slate-400 text-sm leading-relaxed mb-8">
                                {successData?.message}
                            </p>

                            <button
                                onClick={() => setShowSuccessModal(false)}
                                className="w-full py-4 bg-white text-black font-black rounded-2xl hover:bg-slate-200 transition-colors shadow-xl"
                            >
                                Great!
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
